/**
 * Ability runtime state and logic.
 * Manages cooldowns, mana, active buffs, and ability execution.
 */

import {
  type ClassDef,
  type AbilityDef,
  AbilityType,
  AbilitySlot,
} from "./classes";

export type ActiveBuff = {
  abilityId: string;
  remainingTime: number;
  duration: number;
  color: string;
};

export type AbilityState = {
  cooldownRemaining: number; // seconds left
  isReady: boolean;
};

export type CombatState = {
  classDef: ClassDef;
  health: number;
  maxHealth: number;
  mana: number;
  maxMana: number;
  manaRegen: number;
  abilities: [AbilityState, AbilityState, AbilityState, AbilityState];
  activeBuffs: ActiveBuff[];
  globalCooldown: number; // prevents spamming
};

export type AbilityCastResult = {
  success: boolean;
  reason?: string;
  ability?: AbilityDef;
  direction?: { x: number; y: number; z: number };
  position?: { x: number; y: number; z: number };
};

const GLOBAL_COOLDOWN = 0.3; // 300ms between any ability

export function createCombatState(classDef: ClassDef): CombatState {
  return {
    classDef,
    health: classDef.maxHealth,
    maxHealth: classDef.maxHealth,
    mana: classDef.maxMana,
    maxMana: classDef.maxMana,
    manaRegen: classDef.manaRegen,
    abilities: [
      { cooldownRemaining: 0, isReady: true },
      { cooldownRemaining: 0, isReady: true },
      { cooldownRemaining: 0, isReady: true },
      { cooldownRemaining: 0, isReady: true },
    ],
    activeBuffs: [],
    globalCooldown: 0,
  };
}

/**
 * Update cooldowns, mana regen, and buff timers.
 */
export function updateCombatState(state: CombatState, dt: number): void {
  // Mana regen
  state.mana = Math.min(state.maxMana, state.mana + state.manaRegen * dt);

  // Global cooldown
  if (state.globalCooldown > 0) {
    state.globalCooldown = Math.max(0, state.globalCooldown - dt);
  }

  // Ability cooldowns
  for (let i = 0; i < 4; i++) {
    const ability = state.abilities[i];
    if (ability.cooldownRemaining > 0) {
      ability.cooldownRemaining = Math.max(0, ability.cooldownRemaining - dt);
    }
    ability.isReady = ability.cooldownRemaining <= 0;
  }

  // Active buffs
  for (let i = state.activeBuffs.length - 1; i >= 0; i--) {
    state.activeBuffs[i].remainingTime -= dt;
    if (state.activeBuffs[i].remainingTime <= 0) {
      state.activeBuffs.splice(i, 1);
    }
  }
}

/**
 * Attempt to cast an ability.
 */
export function castAbility(
  state: CombatState,
  slot: AbilitySlot,
  casterPos: { x: number; y: number; z: number },
  lookDir: { x: number; y: number; z: number }
): AbilityCastResult {
  const abilityDef = state.classDef.abilities[slot];
  const abilityState = state.abilities[slot];

  // Check global cooldown
  if (state.globalCooldown > 0) {
    return { success: false, reason: "Global cooldown" };
  }

  // Check cooldown
  if (!abilityState.isReady) {
    return { success: false, reason: "On cooldown" };
  }

  // Check mana
  if (state.mana < abilityDef.manaCost) {
    return { success: false, reason: "Not enough mana" };
  }

  // Cast!
  state.mana -= abilityDef.manaCost;
  abilityState.cooldownRemaining = abilityDef.cooldown;
  abilityState.isReady = false;
  state.globalCooldown = GLOBAL_COOLDOWN;

  // Apply buff if ability type is BUFF
  if (abilityDef.type === AbilityType.BUFF && abilityDef.duration > 0) {
    state.activeBuffs.push({
      abilityId: abilityDef.id,
      remainingTime: abilityDef.duration,
      duration: abilityDef.duration,
      color: abilityDef.color,
    });
  }

  return {
    success: true,
    ability: abilityDef,
    direction: lookDir,
    position: casterPos,
  };
}

/**
 * Apply damage to combat state.
 */
export function takeDamage(state: CombatState, amount: number): number {
  // Check for damage reduction buffs
  let reduction = 1.0;
  for (const buff of state.activeBuffs) {
    // Shield Wall / Holy Shield reduce damage
    if (buff.abilityId.includes("shield") || buff.abilityId.includes("fortress")) {
      reduction *= 0.5;
    }
  }

  const finalDamage = Math.floor(amount * reduction);
  state.health = Math.max(0, state.health - finalDamage);
  return finalDamage;
}

/**
 * Heal combat state.
 */
export function heal(state: CombatState, amount: number): void {
  state.health = Math.min(state.maxHealth, state.health + amount);
}
